import Icon from '@components/Icon';
import Text from '@components/Text';
import { styled } from '@linaria/react'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { ITheme } from 'src/theme';


const Container = styled.div`
  display:block;
`;
const HeaderContainer = styled.div<StyleProps>`
  position:relative;
  cursor:pointer;
  display:flex;
  justify-content: space-between;
  align-items: center;
  transition: all .18s linear;
  padding: ${props => props.headerPadding ? props.headerPadding : '24.5px 24px'};
  background-color: ${props => props.showHeaderBackground? (props.headerBackgroundColor ? props.headerBackgroundColor : "#FFF") :"#FFF"};
  border-radius: 16px;
  @media screen and (max-width: 768px) {
    border-radius: 0;
  }
`;
const BottomContainer = styled.div<StyleProps>`
  position:relative;
  margin-left:${props => props.contentMarginLeft ? props.contentMarginLeft: "0"};
  margin-right:${props => props.contentMarginRight ? props.contentMarginRight: "0"};
`;
const TitleContainer = styled.div`
  flex:1;
`;
const ExtraElementContainer = styled.div`
  flex:0;
`;
const IconContainer = styled.div<StyleProps>`
  display:flex;
  align-items: center;
  flex:0;
`;

const ContentContainer = styled.div<StyleProps>`
  height:${props => props.isOpen ? props.height+'' : '0px'};
  overflow:hidden;
	transition: height .18s linear;
`;
const HiddenContainer = styled.div<StyleProps>`
  height: ${props => props.isOpen ? 'auto' : '0px'};
  display: block;
  transition: height .18s linear;
`;
const UnderlineContainer = styled.div<StyleProps>`
  position:absolute;
  left:0;
  right:0;
  bottom:0;
  background-color:${props=>props.bottomLineColor?props.bottomLineColor:"#000"};
  height:${props=>props.bottomLineThickness?props.bottomLineThickness : "0px"};
`;
const HeaderUnderlineContainer = styled.div<StyleProps>`
  border-bottom : 1px solid ${props=>props.bottomLineColor?props.bottomLineColor:"#000"};
  height:${props=>props.bottomLineThickness?props.bottomLineThickness : "0px"};
  margin-left:${props => props.contentMarginLeft ? props.contentMarginLeft: "0"};
  margin-right:${props => props.contentMarginRight ? props.contentMarginRight: "0"};
`;


interface StyleProps {
  height?: string;
  isOpen?: boolean;
  showHeaderBackground?: boolean;
  headerBackgroundColor?: string;
  showBottomLine?: boolean;
  bottomLineThickness?:string;
  bottomLineColor?: string;
  bottomLineClassName?: string;
  contentMarginLeft?:string;
  contentMarginRight?:string;
  showHeaderLine?:boolean;
  headerPadding?: string;
  headerMargin?: string;
}

export interface AccordionProps {
  className?: string | undefined;
  id?: string | undefined;
  theme?: ITheme;
  title?: string | undefined;
  titleClassName?: string | undefined;
  titleFontSize?: number;
  children: React.ReactNode;
  titleColor? : string|undefined;
  headerPadding?: string | undefined;
  headerMargin?: string | undefined;
  showHeaderBackground?: boolean | undefined;
  headerBackgroundColor?: string | undefined;
  contentMarginLeft?:string;
  contentMarginRight?:string;
  showBottomLine?:boolean | undefined;
  showHeaderLine?:boolean | undefined;
  bottomLineClassName?:string | undefined;
  bottomLineColor?:string | undefined;
  bottomLineThickness?: string | undefined;
  isExpanded: boolean;
  showExtraElement?: boolean | undefined;
  extraElement?: JSX.Element | undefined;
}


const Accordion= forwardRef(({children,className,title,titleClassName,titleFontSize,titleColor,showHeaderBackground = false,showHeaderLine=false,headerBackgroundColor,contentMarginLeft,contentMarginRight,showBottomLine = false,bottomLineColor,bottomLineThickness,bottomLineClassName,showExtraElement = false,extraElement,isExpanded = false, headerPadding, headerMargin}:AccordionProps,ref) =>{
  const openIcon = 'carat-up', closeIcon = 'carat-down';
  const [isOpen,setOpen] = useState<boolean>(isExpanded);
  const [icon, setIcon] = useState<string>(isExpanded ? openIcon : closeIcon);
  const [contentHeight,setContentHeight] = useState<string>('auto');
  const contentRef = useRef<HTMLDivElement>(null);

  const clickProcess = useCallback(() => {
      setOpen(previous=>{
          let value = !previous; 
          setIcon(value? openIcon : closeIcon);
          return value;
      });
    }, []);

  useImperativeHandle(ref, () => ({
    closeAccordion() {
      setOpen(previous=>{
        let value = false; 
        setIcon(value? openIcon : closeIcon);
        return value;
      });
    },
    openAccordion() {
      setOpen(previous=>{
        let value = true; 
        setIcon(value? openIcon : closeIcon);
        return value;
      });
    },
  }));

  useEffect(()=>{
    if(contentRef.current?.clientHeight) {
      setContentHeight(contentRef.current?.clientHeight + 'px');
    }
    const handleResize = () => {
      setContentHeight(contentRef.current?.clientHeight + 'px');
    }
    window.addEventListener('resize', handleResize)
  },[isOpen,contentHeight,contentRef]);
  


  return (
    <Container className={className}>
        
      {showHeaderLine && <HeaderUnderlineContainer className={bottomLineClassName} bottomLineThickness={bottomLineThickness} bottomLineColor={bottomLineColor} contentMarginLeft={contentMarginLeft} contentMarginRight={contentMarginRight}/>}
        <HeaderContainer isOpen={isOpen} showHeaderBackground={showHeaderBackground} headerBackgroundColor={headerBackgroundColor} onClick={clickProcess} contentMarginLeft={contentMarginLeft} contentMarginRight={contentMarginRight} headerPadding={headerPadding} headerMargin={headerMargin}>
          <TitleContainer>
            <Text className={titleClassName} color={titleColor?titleColor:"#000"} fontSize={titleFontSize?titleFontSize:20}>{title}</Text>
          </TitleContainer>
          {showExtraElement && <ExtraElementContainer>{extraElement}</ExtraElementContainer>}
          <IconContainer isOpen={isOpen}><Icon name={icon} size="lg"/></IconContainer>
        </HeaderContainer>

        <BottomContainer contentMarginLeft={contentMarginLeft} contentMarginRight={contentMarginRight}>
          <ContentContainer isOpen={isOpen} height={contentHeight} showBottomLine={showBottomLine} bottomLineColor={bottomLineColor}>
            <HiddenContainer  isOpen={isOpen} ref={contentRef}>
              {children}
            </HiddenContainer>
          </ContentContainer>
          {showBottomLine && <UnderlineContainer className={bottomLineClassName} bottomLineThickness={bottomLineThickness} bottomLineColor={bottomLineColor}/>}
        </BottomContainer>
    </Container>
    )
});
export default Accordion;